/**
 * Shared HTTP layer for the question-import endpoints. Used by both
 * admin-console and teacher-app — the wire payload and response shapes are
 * identical; only the auth transport differs.
 *
 * The endpoints are split:
 *   • POST /console/questions/import         → real run, returns ImportResult
 *   • POST /console/questions/import/preview → dry run,  returns ImportPreviewResult
 *
 * Each consumer creates a bound API instance via `createImportApi(transport)`,
 * passing their app's typed fetch helper (e.g. `fetchWithAdmin`,
 * `fetchWithTeacher`). The transport must accept a relative path + RequestInit
 * and return the parsed JSON body typed as `T`.
 */

import type { QuestionDiffResult } from '@padcanvas/shared';

export interface ImportOptions {
    forceUpdate?: boolean;
    importBatchId?: string;
    /** Admin-only: stamp imports as authored by this teacher. */
    onBehalfOfTeacherId?: string;
    tag?: string;
    selectedIds?: string[];
    retainImportBatchId?: boolean;
}

export interface ImportResult {
    imported: number;
    created: number;
    updated: number;
    skipped: number;
    unchanged?: number;
    failed?: number;
    importBatchId?: string;
    warnings?: string[];
    questions: { id: string; status: string; reason?: string; importBatchId?: string }[];
}

/** Re-export of the dry-run response so wrappers don't have to import
 *  `@padcanvas/shared` just to type their preview API. */
export type ImportPreviewResult = QuestionDiffResult;

/** Transport callable shared by admin + teacher apps. Both apps already expose
 *  a typed `fetch` helper with this exact signature (admin's `fetchWithAdmin`,
 *  teacher's `fetchWithTeacher`); the factory just adapts it to import calls. */
export type ImportTransport = <T>(path: string, init: RequestInit) => Promise<T>;

/** Build the JSON body for either import endpoint. The body is identical for
 *  preview and real runs; only the URL differs. */
export function buildImportPayload(
    questions: Record<string, unknown>[],
    images?: Record<string, { data: string; mimeType: string }>,
    options?: ImportOptions,
): Record<string, unknown> {
    const payload: Record<string, unknown> = { questions };
    if (images && Object.keys(images).length > 0) payload.images = images;
    if (options?.forceUpdate) payload.forceUpdate = true;
    if (options?.importBatchId) payload.importBatchId = options.importBatchId;
    if (options?.onBehalfOfTeacherId) payload.onBehalfOfTeacherId = options.onBehalfOfTeacherId;
    if (options?.tag) payload.tag = options.tag;
    if (options?.selectedIds) payload.selectedIds = options.selectedIds;
    if (options?.retainImportBatchId) payload.retainImportBatchId = true;
    return payload;
}

export interface ImportApi {
    importQuestions(
        questions: Record<string, unknown>[],
        images: Record<string, { data: string; mimeType: string }> | undefined,
        options: ImportOptions | undefined,
    ): Promise<ImportResult>;
    previewImportQuestions(
        questions: Record<string, unknown>[],
        images: Record<string, { data: string; mimeType: string }> | undefined,
        options: ImportOptions | undefined,
    ): Promise<ImportPreviewResult>;
}

/** Bind the two import calls to an app's typed-fetch transport. Each app
 *  produces one instance (admin from `fetchWithAdmin`, teacher from
 *  `fetchWithTeacher`) and feeds it into its `ImportQuestionsDialog` wrapper. */
export function createImportApi(transport: ImportTransport): ImportApi {
    const post = <T>(
        path: string,
        questions: Record<string, unknown>[],
        images: Record<string, { data: string; mimeType: string }> | undefined,
        options: ImportOptions | undefined,
    ): Promise<T> => {
        const payload = buildImportPayload(questions, images, options);
        return transport<T>(path, { method: 'POST', body: JSON.stringify(payload) });
    };
    return {
        importQuestions: (questions, images, options) =>
            post<ImportResult>('/console/questions/import', questions, images, options),
        previewImportQuestions: (questions, images, options) =>
            post<ImportPreviewResult>('/console/questions/import/preview', questions, images, options),
    };
}
